import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Axios from "axios";
import Constant from "../../Constant";
import { getAccessToken } from "../../../hooks/AccessToken";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import "../../../css/style.css";

const SubEventsManagement = () => {
  const { id } = useParams();
  const [subEvents, setSubEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchSubEvents = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = getAccessToken();
      const response = await Axios.get(
        Constant.apiBasePath + `simple-sub-events/admin/event/${id}`,
        { headers: { token } }
      );
      if (response.data.status === Constant.statusSuccess) {
        setSubEvents(response.data.data || []);
      } else {
        setError(response.data.message || "Failed to fetch sub events");
      }
    } catch (err) {
      setError("Error fetching sub events");
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchSubEvents();
  }, [fetchSubEvents]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sub event?"))
      return;
    setLoading(true);
    setError("");
    try {
      const token = getAccessToken();
      const response = await Axios.delete(
        Constant.apiBasePath + `simple-sub-events/admin/delete/${id}`,
        { headers: { token } }
      );
      if (response.data.status === Constant.statusSuccess) {
        setSubEvents(subEvents.filter((sub) => sub._id !== id));
      } else {
        setError(response.data.message || "Failed to delete sub event");
      }
    } catch (err) {
      setError("Error deleting sub event");
    }
    setLoading(false);
  };

  const filteredSubEvents =
    filter === "all"
      ? subEvents
      : subEvents.filter((sub) =>
          filter === "active" ? sub.status === 1 : sub.status === 0
        );

  const totalPages = Math.max(
    1,
    Math.ceil((filteredSubEvents.length || 0) / itemsPerPage)
  );
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * itemsPerPage;
  const currentItems = filteredSubEvents.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="content-page contest-submissions-page contest-management-page simple-events-consistent-page">
      <div className="content page-content-spaced">
        <div className="event-management-header">
          <div className="header-content">
            <div className="header-title">
              <h4 className="page-title-heading">Sub Events Management</h4>
              <p className="header-subtitle">
                Manage sub events under the selected simple event.
              </p>
            </div>
            <div className="header-actions">
              <Link to="/simple-events" className="btn openmic-link-btn">
                Back to Simple Events
              </Link>
              <select
                className="form-control filter-select"
                style={{ width: 160 }}
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <Link
                to={`/simple-events/${id}/sub-events/add`}
                className="btn btn-primary btn-add-event contest-add-match"
              >
                + Add New
              </Link>
            </div>
          </div>
        </div>

        <div className="event-management-body">
          <div className="card contest-submissions-card contest-like-card">
            <div className="card-body">
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="table-wrapper contest-submissions-table-responsive contest-like-table-responsive">
                <table className="table event-table contest-submissions-table contest-like-table">
                  <thead>
                    <tr>
                      <th>S.No.</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Video Link</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="table-loading-cell">
                          Loading sub events...
                        </td>
                      </tr>
                    ) : currentItems.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="table-loading-cell">
                          No sub events found.
                        </td>
                      </tr>
                    ) : (
                      currentItems.map((sub, index) => (
                        <tr key={sub._id} className="table-row">
                          <td>{startIndex + index + 1}</td>
                          <td className="namecol">{sub.name}</td>
                          <td className="description">
                            <div className="description-text">{sub.description}</div>
                          </td>
                          <td className="contest-status-col">
                            <span
                              className={`contest-status-pill ${
                                sub.status === 1 ? "is-active" : "is-inactive"
                              }`}
                            >
                              {sub.status === 1 ? "ACTIVE" : "INACTIVE"}
                            </span>
                          </td>
                          <td>{sub.videoLink}</td>
                          <td className="actions-col">
                            <Link
                              to={`/simple-events/${id}/sub-events/edit/${sub._id}`}
                              className="contest-icon-btn open-sessions-icon-btn"
                              aria-label="Edit sub event"
                            >
                              <FontAwesomeIcon icon={faEdit} style={{ fontSize: "13px" }} />
                            </Link>
                            <button
                              className="contest-icon-btn open-sessions-icon-btn danger"
                              onClick={() => handleDelete(sub._id)}
                              type="button"
                              aria-label="Delete sub event"
                            >
                              <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: "13px" }} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                </div>

              <div className="pagination">
                <button
                  type="button"
                  className={safePage === 1 ? "disabled" : "paginate-link"}
                  disabled={safePage === 1}
                  onClick={() => setCurrentPage(safePage - 1)}
                >
                  Previous
                </button>
                <button type="button" className="page-number">
                  {safePage}/{totalPages}
                </button>
                <button
                  type="button"
                  className={safePage === totalPages ? "disabled" : "paginate-link active"}
                  disabled={safePage === totalPages}
                  onClick={() => setCurrentPage(safePage + 1)}
                >
                  Next
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default SubEventsManagement;
